import { byId } from "./domUtils";
import type { PrototypeState } from "./controllerState";
import type { Root } from "./prototypeTypes";
import { getPlanEntitlements } from './planStore';
import { planBadgeHtml, resolveDarkTheme, type PlanTier } from "./planBadge";
import {
	paintRow,
	paintGuestRow,
	shouldNudgeMax,
	formatQuotaPill,
	formatResetDate,
	type AvatarStatusCard,
	type Plan,
} from "./avatarRowPolicy";
import { deriveAvatarInitials } from '../../shared/avatarInitials';

/**
 * Avatar dropdown rendering — single source of truth.
 *
 * History (why this module looks the way it does):
 * The "avatar popup goes blank but stays clickable" bug recurred 6+
 * times because three forces fought over the same DOM:
 *   1. shellMarkup.ts shipped static menu HTML at first paint.
 *   2. avatarView held a second copy injected lazily by an
 *      ensureAvatarDropdownContent helper that only fired when the
 *      container was already empty.
 *   3. Four callers (refreshHomeState, hide/showLoginState,
 *      toggleAvatarDropdown, prototypeHydration) painted in parallel,
 *      and a partial repaint of just `#avatarPlanRow` could leave the
 *      rest of the menu permanently missing.
 *
 * The fix:
 *   - One renderer (renderAvatarDropdown) owns the entire
 *     `#avatarLoggedIn` / `#avatarLoggedOut` content.
 *   - It writes the full HTML on every call (plan row baked in — no
 *     after-the-fact text-node patching). Idempotent and immune to
 *     anything else clearing the container.
 *   - It is invoked at exactly three moments: (a) once at mount to paint
 *     the inline first-frame session, (b) when the user opens the dropdown,
 *     and (c) when authentication state flips. Home / view refreshes never
 *     touch it.
 *
 * Async quota note: opening the dropdown paints a sync status card first
 * (fallback = zero-usage readout "0 / quota used" + bar at 0%), then
 * `refreshAvatarQuota` patches the live used / reset date in place when
 * the lightweight `/api/billing/quota-usage` response arrives. The patch
 * only touches the quota-bearing ids (readout / reset / bar / footer)
 * inside the already-rendered card — it never rewrites the container —
 * so the single-renderer rule above is preserved.
 *
 * If you find yourself adding another writer to `#avatarLoggedIn` or
 * `#avatarLoggedOut`, stop — route the change through this file
 * instead. That is the architectural rule that keeps this bug fixed.
 */

/**
 * Render the avatar mark (initials or vector glyph) inside the
 * `avatarBtn` SVG. For visitors we show a Lucide user-person icon
 * so the avatar has a recognizable default placeholder.
 *
 * Internal helper - exposed only via renderAvatarDropdown.
 */
function paintAvatar(root: Root, state: PrototypeState) {
  const avatar = byId(root, "avatarBtn");
  const mark = byId(root, "avatarMark");
  if (!avatar) return;
  if (state.isLoggedIn) {
    avatar.style.background = "transparent";
    avatar.style.color = "";
    // Restore the full circle + initials SVG (in case it was swapped to visitor icon)
    if (mark && mark.dataset.visitorIcon === "1") {
      mark.setAttribute("viewBox", "0 0 40 40");
      mark.setAttribute("width", "100%");
      mark.setAttribute("height", "100%");
      mark.removeAttribute("style");
      mark.innerHTML = '<defs><linearGradient id="avatarGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="var(--accent)"/><stop offset="100%" stop-color="var(--accent-hover)"/></linearGradient></defs><circle cx="20" cy="20" r="18" fill="url(#avatarGrad)"/><text id="avatarInitials" x="20" y="25" text-anchor="middle" font-family="var(--font-display)" font-size="16" font-weight="600" fill="#fff">?</text>';
      delete mark.dataset.visitorIcon;
    }
    const initials = byId(root, "avatarInitials");
    const text = deriveAvatarInitials(state.runtime?.userDisplayName, state.runtime?.userEmail, state.runtime?.userId);
    if (initials) initials.textContent = text;
  } else {
    // Visitor avatar: person icon + waving hand. The button background
    // and icon color are controlled by CSS (.avatar-btn[data-visitor-icon])
    // so they adapt to the active theme without JS. SVG uses currentColor
    // so stroke follows the CSS `color` property.
    // Clear inline background so the CSS rule takes over (logged-in state
    // sets style.background = "transparent" which would override CSS).
    avatar.style.background = "";
    avatar.style.color = "";
    if (mark && mark.dataset.visitorIcon !== "1") {
      mark.setAttribute("viewBox", "0 0 24 24");
      mark.setAttribute("width", "22");
      mark.setAttribute("height", "22");
      mark.removeAttribute("style");
      mark.innerHTML = '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" stroke-width="2"/><path class="avatar-wave-hand" d="M18 8l1.5-3a1.2 1.2 0 0 0-2.2-1L16 6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
      mark.dataset.visitorIcon = "1";
    }
  }
}

function planFrom(state: PrototypeState): Plan {
  const p = state.effectiveAccessPlan;
  return p === "visitor" || p === "free" ? "free" : p;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Resolved colors for the avatar menu, keyed off the live theme on the
 * <html> element. We resolve to concrete hex here instead of var() so
 * the menu never depends on a CSS variable being correctly defined in
 * its cascade context — one less thing for an outside stylesheet to
 * break.
 */
interface MenuColors {
  label: string;
  detail: string;
  error: string;
  accent: string;
  /** Lite 青（CTA/钩子）。 */
  teal: string;
  /** Max 金（upsell / Max 进度条）。 */
  gold: string;
  /** Pro 进度条填充蓝。 */
  barAccent: string;
  /** 状态卡背景 / 边框 / 进度条轨道。 */
  cardBg: string;
  cardBorder: string;
  barTrack: string;
  panelBg: string;
  panelBorder: string;
}

function resolveMenuColors(): MenuColors {
  const dark = resolveDarkTheme();
  return dark
    ? {
        label: "#f5f5f7",
        detail: "#b0b0b8",
        error: "#ff453a",
        accent: "#0a84ff",
        teal: "#2DD4BF",
        gold: "#FBBF24",
        barAccent: "#60A5FA",
        cardBg: "#1e1e20",
        cardBorder: "rgba(255,255,255,0.05)",
        barTrack: "rgba(255,255,255,0.12)",
        panelBg: "#1c1c1e",
        panelBorder: "#2a2a2a",
      }
    : {
        label: "#1d1d1f",
        detail: "#86868b",
        error: "#ff3b30",
        accent: "#0071e3",
        teal: "#0F766E",
        gold: "#A16207",
        barAccent: "#3B82F6",
        cardBg: "#f5f5f7",
        cardBorder: "rgba(0,0,0,0.04)",
        barTrack: "rgba(0,0,0,0.10)",
        panelBg: "#ffffff",
        panelBorder: "#d2d2d7",
      };
}

/**
 * Inline style that locks menu text into a visible state. Author-origin
 * inline `!important` is the highest-specificity rule available — it
 * beats any selector-based rule, including CSS injected by browser
 * extensions (e.g. immersive-translate) that previously pushed avatar
 * text off-screen via `text-indent` / `clip` / `color` with `!important`
 * and a higher-specificity selector than our defensive class rules.
 *
 * This inline layer is what finally made the recurring "blank but
 * clickable" popup stop coming back: nothing outside this module can
 * hide the text once it is written.
 */
function visibleText(color: string, extra = ""): string {
  return (
    "color:" + color + " !important;" +
    "text-indent:0 !important;" +
    "transform:none !important;" +
    "clip:auto !important;" +
    "opacity:1 !important;" +
    "visibility:visible !important;" +
    "display:block !important;" +
    extra
  );
}

/**
 * Inline style for the thin divider line. Extensions (immersive-translate)
 * were observed injecting `display:none` onto `.av-divider` via a
 * cross-origin stylesheet; lock it visible inline.
 */
function dividerStyle(extra = ""): string {
  return "display:block !important;height:1px !important;visibility:visible !important;" + extra;
}

/* ── 账户状态卡（顶部） ── */

/** 用量读数/低额度提示色：Pro 蓝、Max 金（gold 也用于 Pro 低额度 upsell）。
 *  直接落在卡底上的纯文字色（非胶囊），须 ≥4.5:1（见 spec §7）。
 *  light gold 用 #92400E 而非 #A16207：后者 4.52:1 太贴阈值，留足余量。 */
function quotaColor(tone: 'pro' | 'gold', dark: boolean): string {
  if (tone === 'gold') return dark ? '#FBBF24' : '#92400E';
  return dark ? '#60A5FA' : '#1D4ED8';
}

function arrowSvgHtml(): string {
  return (
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="' +
    'width:11px !important;height:11px !important;flex:none !important;display:inline-block !important;' +
    '"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>'
  );
}

/** AI Create 页配额条同款 gauge 小图标（Lucide），stroke=currentColor 随档位色。
 *  Pro 恒显示（round-4 定稿：Pro=配额仪表盘 / Max=优先闪电）。 */
function gaugeIconSvg(): string {
  return (
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="' +
    'width:13px !important;height:13px !important;flex:none !important;display:inline-block !important;' +
    '"><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>'
  );
}

/** Lucide zap（闪电）图标 — Max 读数的引导图标。round-6：去原生 title
 *  （aria-hidden svg 不弹原生 tooltip + 原生悬停有 ~2s 延时）与 cursor:help（问号光标）；
 *  round-7：tooltip 移到读数容器（quotaReadoutHtml 的 av-zap-pill）上，图标只管画闪电，
 *  悬停闪电或 "88 / 168 used" 文字都即时显示 "Priority generation"（见 avatar-dropdown.css）。
 *  stroke=currentColor 随档位色（Max 金）。 */
function zapIconSvg(): string {
  return (
    '<span class="av-zap" style="' +
    'display:inline-flex !important;align-items:center !important;flex:none !important;' +
    '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="' +
    'width:13px !important;height:13px !important;flex:none !important;display:inline-block !important;' +
    '"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg></span>'
  );
}

/**
 * 配额读数/重置日的骨架占位条（round-5）。同步渲染用固定宽度撑住最终几何，
 * 异步配额返回后就地替换成真实文本 → "0/150 → 88/168" 不再布局跳动。
 * 主题自适应亮片（暗色亮 / 亮色暗）；keyframes 只能定义在 CSS
 * （avatar-quota-shimmer），元素上的 animation 名内联引用。
 */
function quotaSkeletonHtml(dark: boolean, width: number): string {
  const base = dark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)';
  const sheen = dark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)';
  return (
    '<span data-quota-skeleton style="' +
    'display:inline-block !important;flex:none !important;' +
    'width:' + width + 'px !important;height:11px !important;border-radius:3px !important;' +
    'background:linear-gradient(90deg,' + base + ' 25%,' + sheen + ' 50%,' + base + ' 75%) !important;' +
    'background-size:200% 100% !important;' +
    'animation:avatar-quota-shimmer 1.4s linear infinite !important;' +
    '"></span>'
  );
}

/**
 * 把容器里的骨架替换成真实文本（round-5）。骨架在时 replaceWith 文本节点；
 * 无骨架（幂等重入 / 兜底二次写入）则改尾部文本节点 —— 永远不清容器，
 * 保住引导图标（Pro gauge / Max zap，round-4 bug 教训）。
 */
function patchQuotaText(container: HTMLElement, text: string): void {
  const skeleton = container.querySelector('[data-quota-skeleton]');
  if (skeleton) {
    skeleton.replaceWith(document.createTextNode(text));
    return;
  }
  const textNode = [...container.childNodes].filter((n) => n.nodeType === 3).pop();
  if (textNode) textNode.textContent = text;
  else container.appendChild(document.createTextNode(text));
}

/**
 * 右上角用量读数。仅 Pro/Max 渲染（实时额度）— Guest/Free/Lite 的右上角
 * CTA 已删（底部钩子承载动作）。**非胶囊**：引导图标 + "0 / 150 used"，
 * 与 AI Create 页配额条同款视觉（round-3 用户反馈）。图标 stroke=currentColor，
 * 随档位色（Pro 蓝 / Max 金 / Pro 低额度金）。引导图标按档位（round-4 定稿）：
 * Pro 恒 gauge，Max 恒 zap（闪电，悬停即时 tooltip "Priority generation"，round-6）。
 */
function quotaReadoutHtml(card: AvatarStatusCard, c: MenuColors, dark: boolean): string {
  if (!card.hasQuota) return '';
  const color = quotaColor(card.plan === 'max' ? 'gold' : 'pro', dark);
  const isZap = card.readoutIcon === 'zap';
  const icon = isZap ? zapIconSvg() : gaugeIconSvg();
  // round-7: tooltip 挂读数容器（#avatarQuotaPill）上 —— 悬停闪电图标或
  // "88 / 168 used" 文字都即时显示 "Priority generation"（不再只限图标）。
  // 容器 position:relative 作 tooltip 定位锚；av-zap-pill 标记 Max 档以命中
  // CSS 的 hover/手型光标规则（Pro gauge 无 tooltip、保持默认光标不误触）。
  const tip = isZap
    ? '<span class="av-zap-tip" role="tooltip">' + escapeAttr(card.priorityLabel) + '</span>'
    : '';
  // round-5: 同步渲染骨架（data-quota-skeleton）而非 "0 / N used" 文本；min-width
  // 锁住读数位总宽（图标 13 + gap 4 + 文本 84），异步替换后宽度不变 → 无跳动。
  return (
    '<span class="info-pill' + (isZap ? ' av-zap-pill' : '') + '" id="avatarQuotaPill" style="' +
    'display:inline-flex !important;align-items:center !important;gap:4px !important;' +
    'position:relative !important;' +
    'min-width:101px !important;' +
    'font-size:11px !important;font-weight:650 !important;' +
    'white-space:nowrap !important;' +
    'color:' + color + ' !important;' +
    'text-indent:0 !important;opacity:1 !important;visibility:visible !important;' +
    '">' + icon + quotaSkeletonHtml(dark, 84) + tip + '</span>'
  );
}

function barHtml(progress: number, plan: Plan, c: MenuColors, dark: boolean): string {
  const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
  const fill = plan === 'max' ? c.gold : c.barAccent;
  return (
    '<div class="s-bar" id="avatarQuotaBar" style="' +
    'height:5px !important;border-radius:3px !important;margin-top:7px !important;' +
    'overflow:hidden !important;background:' + c.barTrack + ' !important;' +
    'visibility:visible !important;opacity:1 !important;' +
    '"><div class="s-bar-fill" id="avatarQuotaBarFill" style="' +
    'width:' + pct + '% !important;height:100% !important;border-radius:3px !important;' +
    'background:' + fill + ' !important;' +
    '"></div></div>'
  );
}

function footerHtml(card: AvatarStatusCard, c: MenuColors): string {
  const tone = card.footerTone;
  const color =
    tone === 'accent' ? c.accent
      : tone === 'teal' ? c.teal
      : tone === 'gold' ? c.gold
      : c.detail;
  const weight = tone === 'gold' ? '650' : '500';
  const arrow = tone !== 'neutral' ? arrowSvgHtml() : '';
  return (
    '<div class="s-hook" id="avatarQuotaFooter" style="' +
    'color:' + color + ' !important;' +
    'text-indent:0 !important;transform:none !important;clip:auto !important;' +
    'opacity:1 !important;visibility:visible !important;' +
    'display:flex !important;align-items:center !important;gap:3px !important;' +
    'font-size:11px !important;font-weight:' + weight + ' !important;' +
    'margin-top:6px !important;line-height:1.4 !important;' +
    '">' + escapeAttr(card.footer) + arrow + '</div>'
  );
}

/**
 * 状态行：Guest/Free/Lite 单行全宽；Pro/Max 为两栏 —— 左 "AI private
 * chapters"，右重置日（#avatarQuotaReset，异步补丁换成真实日期）。
 */
function detailRowHtml(card: AvatarStatusCard, c: MenuColors, dark: boolean): string {
  const base = 'font-size:11px;line-height:1.4;';
  if (!card.hasQuota) {
    return (
      '<div class="s-detail" id="avatarPlanDetail" style="' + visibleText(c.detail, 'margin-top:4px;' + base) + '">' +
      escapeAttr(card.detail) +
      '</div>'
    );
  }
  // round-5: 重置日同步渲染骨架（min-width + 固定 height 锁住最终几何），异步
  // 配额返回后就地替换成真实日期（"Resets Sep 8"）；justify-content:flex-end
  // 让文本右缘始终贴齐面板边，左缘留白不构成跳动。
  return (
    '<div class="s-detail-row" style="' +
    'display:flex !important;align-items:baseline !important;justify-content:space-between !important;' +
    'gap:8px !important;margin-top:4px !important;visibility:visible !important;' +
    '">' +
    '<div class="s-detail" id="avatarPlanDetail" style="' + visibleText(c.detail, base) + '">' + escapeAttr(card.detail) + '</div>' +
    '<span class="s-detail-meta" id="avatarQuotaReset" style="' +
    'color:' + c.detail + ' !important;' +
    'text-indent:0 !important;transform:none !important;clip:auto !important;' +
    'opacity:1 !important;visibility:visible !important;' +
    'display:inline-flex !important;align-items:center !important;justify-content:flex-end !important;' +
    'font-size:11px !important;line-height:1.4 !important;white-space:nowrap !important;' +
    'min-width:88px !important;height:16px !important;' +
    '">' + quotaSkeletonHtml(dark, 88) + '</span>' +
    '</div>'
  );
}

/**
 * Pro/Max 底部行：Max 无底部文案（"Priority generation" 已上移为读数前的
 * 闪电图标，round-4）；Pro 默认空（隐藏），仅低额度（≤20% 剩余）时由
 * refreshAvatarQuota 异步填入 Max upsell。重置日已上移 detail 行右侧
 * （#avatarQuotaReset），不再出现在这里。
 */
function quotaFooterHtml(card: AvatarStatusCard, c: MenuColors): string {
  const text = card.footer;
  const visible = text !== '';
  return (
    '<div class="s-hook" id="avatarQuotaFooter" style="' +
    'color:' + c.detail + ' !important;' +
    'text-indent:0 !important;transform:none !important;clip:auto !important;' +
    'opacity:1 !important;visibility:visible !important;' +
    'display:' + (visible ? 'flex' : 'none') + ' !important;align-items:center !important;gap:3px !important;' +
    'font-size:11px !important;font-weight:500 !important;' +
    'margin-top:6px !important;line-height:1.4 !important;' +
    '">' + (visible ? escapeAttr(text) : '') + '</div>'
  );
}

/**
 * 状态卡主体：左上=身份徽章，右上=用量读数（Pro/Max），
 * 中间=状态行（Pro/Max 为 detail+重置日两栏），Pro/Max 追加进度条，
 * 底部=钩子/信息行（Pro/Max 走 quotaFooterHtml）。
 * 整卡内联样式（含 !important），延续"防外部 stylesheet 隐藏"的防御。
 */
function statusCardHtml(tier: PlanTier, plan: Plan, card: AvatarStatusCard, clickAction: string, c: MenuColors): string {
  const dark = resolveDarkTheme();
  const badge = planBadgeHtml(tier, dark, ' id="avatarPlanName" data-plan-label="' + plan + '"');
  const readout = quotaReadoutHtml(card, c, dark);
  // round-6: 空 action（Max 已是最高档）→ 卡不响应点击，光标改 default，data-proto-click
  // 也不渲染（点击分发器找不到可点元素即 no-op）。非空（Guest→login / Pro 及以下→paywall）
  // 保留 pointer + 点击。
  const hasAction = clickAction !== '';
  const clickAttr = hasAction ? ' data-proto-click="' + clickAction + '"' : '';
  const lines = [
    '<div class="s-row1" style="display:flex !important;align-items:center !important;justify-content:space-between !important;gap:8px !important;visibility:visible !important;">' + badge + readout + '</div>',
    detailRowHtml(card, c, dark),
  ];
  if (card.hasQuota) lines.push(barHtml(card.progress, plan, c, dark));
  lines.push(card.hasQuota ? quotaFooterHtml(card, c) : footerHtml(card, c));
  return (
    '<div class="av-status-card"' + clickAttr + ' id="avatarPlanRow" data-plan="' + plan + '" style="' +
    'background:' + c.cardBg + ' !important;' +
    'border:1px solid ' + c.cardBorder + ' !important;' +
    'border-radius:10px !important;' +
    'padding:10px 12px !important;' +
    'margin-bottom:4px !important;' +
    'cursor:' + (hasAction ? 'pointer' : 'default') + ' !important;' +
    'visibility:visible !important;' +
    '">' + lines.join('') + '</div>'
  );
}

/**
 * Build the logged-in menu's full HTML, including a fully-populated
 * status card. The card is never patched after-the-fact - every call
 * produces a self-contained string with the correct badge/pill/detail/
 * footer baked in. This is what makes the renderer immune to
 * partial-write bugs.
 *
 * Clicking the card opens the paywall modal, which contains the unified
 * subscription panel (buildSubscriptionPanel). The avatar dropdown itself
 * only shows a summary - the full tier grid lives in the paywall so the
 * user sees it on demand, not on every dropdown open.
 */
function loggedInMenuHtml(plan: Plan, c: MenuColors): string {
	const entitlement = getPlanEntitlements(plan);
  const card = paintRow(plan, entitlement.privateChaptersPerMonth, entitlement.githubDeepDive);
  // round-6: Max 已是最高档，点状态卡不再跳订阅面板（showPaywall）——空 action 让卡
  // 整体不响应点击（statusCardHtml 也不渲染 data-proto-click、光标改 default）。
  // Pro 及以下保留：点卡打开订阅面板（Pro 低额度引导升 Max）。
  const cardAction = plan === 'max' ? '' : 'showPaywall();closeAvatarDropdown()';
  return (
    statusCardHtml(plan, plan, card, cardAction, c) +
    '<div class="av-divider" style="' + dividerStyle() + '"></div>' +
    inviteRowHtml(c) +
    profileRowHtml(c) +
    feedbackRowHtml(c) +
    '<div class="av-divider" style="' + dividerStyle() + '"></div>' +
    logoutRowHtml(c)
  );
}

function guestCardHtml(c: MenuColors): string {
  return statusCardHtml('guest', 'free', paintGuestRow(), 'showLoginModal();closeAvatarDropdown()', c);
}

function inviteRowHtml(c: MenuColors): string {
  return (
    '<div class="av-sub" data-proto-click="showView(\'referrals\');setActiveTab(\'profile\');closeAvatarDropdown()">' +
    '<div style="display:flex;align-items:center;gap:8px;"><div class="av-label" style="' + visibleText(c.label) + '">Invite friends</div><span class="av-invite-gift-wrap" aria-hidden="true" data-icon-source="lucide"><img class="av-invite-gift" src="/assets/icons/lucide-gift.svg" alt=""></span></div>' +
    '<div class="av-detail" style="' + visibleText(c.detail) + '">Earn AI Create credits together</div>' +
    '</div>'
  );
}

function profileRowHtml(c: MenuColors): string {
  return (
    '<div class="av-sub" data-proto-click="showView(\'profile\');closeAvatarDropdown()">' +
    '<div class="av-label" style="' + visibleText(c.label) + '">Profile</div>' +
    '<div class="av-detail" style="' + visibleText(c.detail) + '">View your activity and settings</div>' +
    '</div>'
  );
}

function feedbackRowHtml(c: MenuColors): string {
  return (
    '<div class="av-sub" data-proto-click="showView(\'feedback\');closeAvatarDropdown()">' +
    '<div class="av-label" style="' + visibleText(c.label) + '">Send Feedback</div>' +
    '<div class="av-detail" style="' + visibleText(c.detail) + '">Your voice shapes this product</div>' +
    '</div>'
  );
}

function logoutRowHtml(c: MenuColors): string {
  return (
    '<div class="av-sub" data-proto-click="handleLogout()">' +
    '<div class="av-label" style="' + visibleText(c.error) + '">Log Out</div>' +
    '</div>'
  );
}

function loggedOutMenuHtml(c: MenuColors): string {
  return (
    guestCardHtml(c) +
    '<div class="av-divider" style="' + dividerStyle('margin:4px 0;') + '"></div>' +
    '<div class="av-sub" data-proto-click="showLoginModal();closeAvatarDropdown()">' +
    '<div class="av-label" style="' + visibleText(c.label) + '">Log In</div>' +
    '<div class="av-detail" style="' + visibleText(c.detail) + '">Email or social sign-in</div>' +
    '</div>' +
    '<div class="av-divider" style="' + dividerStyle('margin:4px 0;') + '"></div>' +
    profileRowHtml(c) +
    feedbackRowHtml(c)
  );
}

/**
 * 打开下拉后异步拉取实时 AI 额度并就地补丁状态卡（Pro/Max 专属）。
 * 仅触达卡内配额 id（pill / 进度条 / 底部钩子），不重写整个容器，
 * 因此不破坏 renderAvatarDropdown 单一渲染者规则。
 *
 * 竞态守卫：
 * - 响应到达时下拉已关闭 → 不落笔（下一次打开会重新渲染 + 重拉）。
 * - viewer 在请求期间切换 plan → 不落笔。
 * - 兜底失败（API 异常 / limit 非法）→ 保留同步渲染的 plan 目录兜底文案。
 */
export async function refreshAvatarQuota(root: Root, state: PrototypeState): Promise<void> {
  const plan = planFrom(state);
  if (plan !== 'pro' && plan !== 'max') return;
  const api = state.runtime?.api;
  if (!api || typeof api.getQuotaUsage !== 'function') return;
  let response: Awaited<ReturnType<typeof api.getQuotaUsage>>;
  try {
    response = await api.getQuotaUsage(1);
  } catch {
    // round-5: API 失败时把骨架替换成同步兜底值，绝不让骨架永久停留。
    writeFallbackQuota(root, plan);
    return;
  }
  const dropdown = byId(root, 'avatarDropdown');
  if (!dropdown || !dropdown.classList.contains('open')) return;
  if (planFrom(state) !== plan) return;

  const summary = response.summary;
  const total = summary.limit;
  if (!Number.isSafeInteger(total) || total <= 0) return;
  // 用量计数（从 0 向上增）：pill 与进度条都表达"已用多少"，
  // 避免 "150 / 150 left" 满格误导新用户。nudge 仍按剩余判断。
  const used = Math.max(0, Number(summary.used) || 0);
  const remaining = Math.max(0, Number(summary.remaining) || 0);
  const resetDate = summary.resetDate ?? null;
  const low = shouldNudgeMax(remaining, total);
  const dark = resolveDarkTheme();
  const c = resolveMenuColors();
  const date = formatResetDate(resetDate);

  const readout = byId(root, 'avatarQuotaPill');
  if (readout) {
    // round-5: 骨架→真实文本原地替换（patchQuotaText），不再走尾部文本节点——
    // 同步渲染现在是骨架而非文本。保留引导图标（Pro gauge / Max zap）。
    const text = formatQuotaPill(used, total);
    patchQuotaText(readout, text);
    readout.style.color = quotaColor(plan === 'max' || low ? 'gold' : 'pro', dark) + ' !important';
  }

  const reset = byId(root, 'avatarQuotaReset');
  if (reset) {
    patchQuotaText(reset, date ? 'Resets ' + date : 'Resets monthly');
  }

  const barFill = byId(root, 'avatarQuotaBarFill');
  if (barFill) {
    const pct = Math.max(0, Math.min(100, Math.round((used / total) * 100)));
    barFill.style.width = pct + '%';
    barFill.style.background = (plan === 'max' || low ? c.gold : c.barAccent) + ' !important';
  }

  const footer = byId(root, 'avatarQuotaFooter');
  if (footer) {
    if (plan === 'pro' && low) {
      const maxQuota = getPlanEntitlements('max').privateChaptersPerMonth;
      footer.innerHTML = escapeAttr('Running low · Max gives ' + maxQuota + '/mo + Priority') + arrowSvgHtml();
      footer.style.color = c.gold + ' !important';
      footer.style.fontWeight = '650 !important';
      footer.style.display = 'flex !important';
    } else {
      // Max 无底部文案（Priority generation 已上移为读数前的闪电图标，round-4）；
      // Pro 常态也无 —— 仅低额度 nudge 填上面的 upsell。
      footer.textContent = '';
      footer.style.display = 'none !important';
    }
  }
}

/**
 * 配额 API 失败兜底（round-5）：把骨架替换成同步兜底值（0 用量 + "Resets
 * monthly" + 0% 填充），避免骨架在 API 异常时永久停留。与同步渲染同源
 * （formatQuotaPill(0, N) = paintRow 的 pill 兜底文案）。
 */
function writeFallbackQuota(root: Root, plan: Plan): void {
  const dropdown = byId(root, 'avatarDropdown');
  if (!dropdown || !dropdown.classList.contains('open')) return;
  const dark = resolveDarkTheme();
  const c = resolveMenuColors();
  const readout = byId(root, 'avatarQuotaPill');
  if (readout) {
    patchQuotaText(readout, formatQuotaPill(0, getPlanEntitlements(plan).privateChaptersPerMonth));
    readout.style.color = quotaColor(plan === 'max' ? 'gold' : 'pro', dark) + ' !important';
  }
  const reset = byId(root, 'avatarQuotaReset');
  if (reset) patchQuotaText(reset, 'Resets monthly');
  const barFill = byId(root, 'avatarQuotaBarFill');
  if (barFill) {
    barFill.style.width = '0%';
    barFill.style.background = (plan === 'max' ? c.gold : c.barAccent) + ' !important';
  }
}

/**
 * The single entry point that paints the avatar dropdown. Call it
 * whenever the menu is about to become visible OR whenever auth state
 * flips. It is idempotent: every invocation rewrites the full menu
 * content from `state` alone — no caches, no DOM-as-state.
 */
export function renderAvatarDropdown(root: Root, state: PrototypeState) {
  const loggedIn = byId(root, "avatarLoggedIn");
  const loggedOut = byId(root, "avatarLoggedOut");
  const dropdown = byId(root, "avatarDropdown");
  const c = resolveMenuColors();

  // Lock the panel background inline so no outside stylesheet (incl.
  // browser-extension CSS, e.g. immersive-translate's dark-mode
  // overrides) can flip it to a color that clashes with the inline text
  // colors baked into the menu HTML. MUST use setProperty with
  // "important" — a bare `.style.background = "x"` (no important) is
  // beaten by any selector rule carrying !important, which is exactly
  // how the extension was re-painting the panel light under a dark
  // theme and hiding the light-colored text. Inline !important is the
  // ceiling; nothing outside this module can override it.
  if (dropdown) {
    dropdown.style.background = c.panelBg + " !important";
    dropdown.style.backgroundColor = c.panelBg + " !important";
    dropdown.style.borderColor = c.panelBorder + " !important";
  }

  // Containers must stay transparent so the panel bg shows through.
  // Also !important — otherwise an extension can paint them opaque and
  // cover the locked panel background.
  const lockTransparent = (el: HTMLElement | null | undefined) => {
    if (!el) return;
    el.style.background = "transparent !important";
    el.style.backgroundColor = "transparent !important";
  };

  if (state.isLoggedIn) {
    if (loggedIn) {
      loggedIn.innerHTML = loggedInMenuHtml(planFrom(state), c);
      loggedIn.style.display = "block";
      lockTransparent(loggedIn);
    }
    if (loggedOut) {
      loggedOut.innerHTML = "";
      loggedOut.style.display = "none";
      lockTransparent(loggedOut);
    }
  } else {
    if (loggedOut) {
      loggedOut.innerHTML = loggedOutMenuHtml(c);
      loggedOut.style.display = "block";
      lockTransparent(loggedOut);
    }
    if (loggedIn) {
      loggedIn.innerHTML = "";
      loggedIn.style.display = "none";
      lockTransparent(loggedIn);
    }
  }

  paintAvatar(root, state);
}
